import { Box, Typography } from "@mui/material";
import EducationShell from "../../components/education/EducationShell";

// ─── Palette — matches EducationLanding.jsx / TypesLanding.jsx ───────────────
const INK = "#0F172A";
const SLATE = "#475569";
const MUTED = "#64748B";
const BORDER = "#E2E8F0";
const ACCENT = "#0F766E";
const ACCENT_SOFT = "#F0FDFA";
const PAPER = "#FFFFFF";
const CANVAS = "#F8FAFC";

const LABS = [
  {
    code: "eGFR",
    name: "Estimated Glomerular Filtration Rate",
    what: "eGFR measures how well your kidneys are filtering waste from your blood. It's calculated from your creatinine level, age, and sex, and is the primary number used to stage CKD.",
    ranges: [
      { label: "≥ 90", note: "Normal kidney function (Stage 1 if protein is also present in urine)" },
      { label: "60–89", note: "Mildly reduced (Stage 2)" },
      { label: "45–59", note: "Mildly to moderately reduced (Stage 3a)" },
      { label: "30–44", note: "Moderately to severely reduced (Stage 3b)" },
      { label: "15–29", note: "Severely reduced (Stage 4)" },
      { label: "< 15", note: "Kidney failure (Stage 5)" },
    ],
    why: "A single eGFR value is a snapshot — the trend over time is what tells your care team whether your CKD is stable or progressing. A drop of more than a few points per year generally warrants closer attention.",
  },
  {
    code: "UACR",
    name: "Urine Albumin-to-Creatinine Ratio",
    what: "UACR measures how much albumin (a protein) is leaking into your urine — often an early sign of kidney damage that can appear before eGFR declines.",
    ranges: [
      { label: "< 30 mg/g", note: "Normal to mildly increased" },
      { label: "30–300 mg/g", note: "Moderately increased (microalbuminuria)" },
      { label: "> 300 mg/g", note: "Severely increased (macroalbuminuria)" },
    ],
    why: "Higher UACR is linked to faster CKD progression and higher cardiovascular risk, independent of eGFR. It's used together with eGFR to determine your overall CKD risk category, and is a key factor in whether treatments like SGLT2 inhibitors or finerenone are recommended.",
  },
  {
    code: "K+",
    name: "Potassium",
    what: "Potassium is an electrolyte that healthy kidneys help regulate. As CKD progresses, especially in later stages, kidneys become less able to remove excess potassium, which can build up to dangerous levels (hyperkalemia).",
    ranges: [
      { label: "3.5–5.0 mEq/L", note: "Typical normal range (labs vary slightly)" },
      { label: "> 5.0 mEq/L", note: "Elevated — may require dietary or medication changes" },
      { label: "> 6.0 mEq/L", note: "Significantly elevated — can affect heart rhythm; contact your care team promptly" },
    ],
    why: "Severely elevated potassium can cause dangerous heart rhythm changes. This is one reason regular monitoring matters, especially if you're on an ACE inhibitor, ARB, or finerenone — medications that protect the kidneys but can also raise potassium.",
  },
  {
    code: "PTH",
    name: "Parathyroid Hormone",
    what: "PTH helps regulate calcium, phosphorus, and vitamin D balance. In CKD, especially Stages 3-5, failing kidneys disrupt this balance, often leading to CKD-mineral and bone disorder (CKD-MBD).",
    ranges: [
      { label: "Normal", note: "Typically checked alongside phosphorus, calcium, and vitamin D" },
      { label: "Elevated", note: "May indicate developing bone/mineral disorder, more common from Stage 3 onward" },
    ],
    why: "As kidney function declines, phosphorus builds up and active vitamin D production drops, triggering the parathyroid glands to release more PTH to compensate. Over time this can weaken bones and contribute to cardiovascular calcification.",
  },
  {
    code: "Hgb",
    name: "Hemoglobin",
    what: "Hemoglobin measures your red blood cell count. Anemia (low hemoglobin) is common in CKD because failing kidneys produce less erythropoietin (EPO), a hormone that signals bone marrow to make red blood cells.",
    ranges: [
      { label: "Normal", note: "Varies by age and sex — ask your care team for your specific target range" },
      { label: "Low", note: "May indicate CKD-related anemia, especially in later stages" },
    ],
    why: "Anemia contributes to the fatigue and shortness of breath many CKD patients experience, and is linked to increased cardiovascular risk if left untreated. Iron supplementation and erythropoiesis-stimulating agents (ESAs) are common treatments once identified.",
  },
];

export default function LabsGuidePage() {
  return (
    <EducationShell breadcrumbs={[{ label: "Learn", path: "/education" }, { label: "Lab Results Guide" }]}>
      <Box sx={{ fontFamily: "'DM Sans', sans-serif", bgcolor: PAPER }}>
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=Fraunces:ital,wght@0,300;0,600;1,300&display=swap');
        `}</style>

        {/* Intro */}
        <Box sx={{ px: { xs: 3, md: 8 }, pt: { xs: 5, md: 7 }, pb: { xs: 4, md: 5 }, borderBottom: `1px solid ${BORDER}` }}>
          <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, letterSpacing: "0.14em", textTransform: "uppercase", color: ACCENT, mb: 1.5 }}>
            Lab Results Guide
          </Typography>
          <Typography sx={{ fontFamily: "'Fraunces', serif", fontSize: { xs: "1.7rem", md: "2.3rem" }, fontWeight: 600, color: INK, mb: 2, maxWidth: 640 }}>
            Understanding your CKD lab values
          </Typography>
          <Typography sx={{ fontSize: "0.95rem", color: SLATE, maxWidth: 620, lineHeight: 1.7 }}>
            These are the labs your nephrologist tracks most closely to monitor your kidney disease. Knowing what
            each one means — and what the numbers indicate — can help you follow your own results and ask more
            informed questions at appointments.
          </Typography>
        </Box>

        {/* Lab cards */}
        <Box sx={{ px: { xs: 3, md: 8 }, py: { xs: 6, md: 8 } }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {LABS.map((lab) => (
              <Box key={lab.code} sx={{ border: `1px solid ${BORDER}`, borderRadius: "12px", p: { xs: 3, md: 4 }, bgcolor: PAPER }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                  <Box
                    sx={{
                      display: "inline-flex",
                      px: 1.5,
                      py: 0.5,
                      borderRadius: "6px",
                      bgcolor: ACCENT_SOFT,
                      color: ACCENT,
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      letterSpacing: "0.04em",
                    }}
                  >
                    {lab.code}
                  </Box>
                  <Typography sx={{ fontFamily: "'Fraunces', serif", fontSize: { xs: "1.15rem", md: "1.35rem" }, fontWeight: 600, color: INK }}>
                    {lab.name}
                  </Typography>
                </Box>

                <Typography sx={{ fontSize: "0.92rem", color: SLATE, lineHeight: 1.75, mb: 3, maxWidth: 760 }}>
                  {lab.what}
                </Typography>

                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: `repeat(${Math.min(lab.ranges.length, 3)}, 1fr)` }, gap: 2, mb: 3 }}>
                  {lab.ranges.map((r) => (
                    <Box key={r.label} sx={{ border: `1px solid ${BORDER}`, borderRadius: "8px", p: 2, bgcolor: CANVAS }}>
                      <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: INK, mb: 0.5 }}>
                        {r.label}
                      </Typography>
                      <Typography sx={{ fontSize: "0.8rem", color: SLATE, lineHeight: 1.5 }}>
                        {r.note}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                <Box sx={{ borderLeft: `3px solid ${ACCENT}`, pl: 2.5 }}>
                  <Typography sx={{ fontSize: "0.68rem", fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: ACCENT, mb: 0.75 }}>
                    Why it matters
                  </Typography>
                  <Typography sx={{ fontSize: "0.88rem", color: SLATE, lineHeight: 1.7, maxWidth: 760 }}>
                    {lab.why}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </EducationShell>
  );
}